import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { errorKey } from '../../../data/studioApi';
import { normalizeDocument } from '../../../features/ArtEditor/headless';
import { answerOf, generationOffered, imageChoice, initialChoice, isChosen, nextPending, openImageQuestion } from '../format';

const REQUOTE = ['usage_quote_consumed', 'usage_quote_mismatch', 'usage_quote_expired'];
const IDLE = { status: 'idle', key: null, value: null, error: null };
// The choices outlive the dialog (running out of credits puts the plans in its place), per
// question and version: reopening it finds them as they were left.
const kept = new Map();

// The image question's state (dialogs/ImageChoiceDialog): the place being edited (kept in
// `studio.modal.slot`, so the art panel brings it to the stage too; it opens on the first
// place still to answer), one choice per place (the AI's default, else none yet), the
// subjects the creator rewrote for a generation, the photos sent from the dialog, and the
// answer. Every place needs a choice first (2.12.0): until then confirming goes to the next
// place left. With a generation one confirmation sends the quote (`s.imageQuote`) and then
// `s.chooseImages` with it: the ceiling the creator confirms is the one the footer shows. A
// quote above it, or one that expired, was used or no longer matches, is shown to confirm
// again. Out of credits is the hook's (the plans).
export default function useImageChoice(s) {
  const { setModal } = s;
  const action = openImageQuestion(s.run);
  const places = useMemo(() => action?.images || [], [action]);
  const document = action?.preview?.document;
  const draft = useMemo(() => (document ? normalizeDocument(document) : null), [document]);
  const stamp = action ? `${action.id}:${action.version}` : null;
  const restored = stamp ? kept.get(stamp) : null;
  const [choices, setChoices] = useState(() => restored?.choices || Object.fromEntries(places.map(place => [place.slot_id, initialChoice(place, s.features)])));
  const [subjects, setSubjects] = useState(() => restored?.subjects || {});
  const [start] = useState(() => places.find(place => !isChosen(place, choices[place.slot_id]))?.slot_id || places[0]?.slot_id || null);
  const [added, setAdded] = useState(null);
  const [quote, setQuote] = useState(IDLE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const alive = useRef(true);
  const quoteRequest = useRef(0);
  // While the answer is being sent the dialog stays: closed, a failure would have nowhere to show.
  const sending = useRef(false);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => { if (stamp) kept.set(stamp, { choices, subjects }); }, [stamp, choices, subjects]);

  const selectedId = places.some(place => place.slot_id === s.modal?.slot) ? s.modal.slot : start;
  const place = places.find(item => item.slot_id === selectedId) || null;
  const select = useCallback(id => setModal({ type: 'images', slot: id }), [setModal]);
  const close = useCallback(() => { if (!sending.current) setModal(null); }, [setModal]);
  const pending = places.filter(item => !isChosen(item, choices[item.slot_id]));
  // "Próxima imagem": the next place still to answer after this one.
  const next = () => { const id = nextPending(places, choices, selectedId); if (id) select(id); };

  const setChoice = (slotId, choice) => { setError(null); setNotice(null); setChoices(previous => ({ ...previous, [slotId]: choice })); };
  // A picked image toggles: picking it again leaves the place to answer again.
  const pickImage = image => {
    if (!place) return;
    const current = choices[place.slot_id];
    const picked = imageChoice(image);
    const same = current?.source === picked.source && (picked.source === 'asset' ? current.asset_id === picked.asset_id : current.community_image_id === picked.community_image_id);
    setChoice(place.slot_id, same ? null : picked);
  };
  const generate = () => { if (place && generationOffered(s.features, place)) setChoice(place.slot_id, { source: 'generate' }); };
  const keep = () => { if (place) setChoice(place.slot_id, { source: 'keep' }); };
  const clear = () => { if (place) setChoice(place.slot_id, null); };
  const setSubject = text => { if (place) setSubjects(previous => ({ ...previous, [place.slot_id]: text })); };
  // A photo sent from the dialog is the creator's like any upload: it goes in the place and
  // leads their uploads in the gallery below.
  const upload = async files => {
    const slotId = place?.slot_id;
    const image = await s.art.images.upload(files[0]);
    if (!alive.current) return [image];
    if (slotId) setChoice(slotId, imageChoice(image));
    setAdded([image]);
    return [image];
  };

  const generating = places.filter(item => choices[item.slot_id]?.source === 'generate');
  const generateIds = generating.map(item => item.slot_id);
  const generateKey = generateIds.join();
  const estimate = generating.reduce((sum, item) => sum + (item.generation_credits || 0), 0);
  // A quote covers exactly the places to generate: another set needs another one.
  useEffect(() => {
    if (quote.key === generateKey) return;
    quoteRequest.current += 1;
    setQuote(IDLE);
  }, [generateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The quote, or null when it failed (the footer says why) or a newer one was asked.
  const requestQuote = async () => {
    if (!action || !generateIds.length) return null;
    const request = ++quoteRequest.current;
    const key = generateKey;
    setQuote({ status: 'loading', key, value: null, error: null });
    try {
      const value = await s.imageQuote(action, generateIds);
      if (!alive.current || quoteRequest.current !== request) return null;
      setQuote({ status: 'ready', key, value, error: null });
      return value;
    } catch (failure) {
      if (alive.current && quoteRequest.current === request) setQuote({ status: 'error', key, value: null, error: errorKey(failure) });
      return null;
    }
  };

  // First every place, then one confirmation: with a generation the quote and, within the
  // ceiling shown, the answer with it right away. The dialog closes once accepted.
  const confirm = async () => {
    if (!action || submitting || quote.status === 'loading') return;
    if (pending.length) { select(pending.find(item => item.slot_id !== selectedId)?.slot_id || pending[0].slot_id); return; }
    setSubmitting(true);
    setError(null);
    setNotice(null);
    sending.current = true;
    try {
      // A quote already on screen was the ceiling confirmed; a new one above the estimate
      // shown is not, so it stops for the creator to confirm it.
      const shown = quote.status === 'ready' && quote.key === generateKey ? quote.value : null;
      const quoted = generateIds.length ? shown || await requestQuote() : null;
      if (generateIds.length && !quoted) return;
      if (quoted && !shown && quoted.quoted_units > estimate) { setNotice('requoted'); return; }
      const done = await s.chooseImages(action, answerOf(places, choices, subjects), quoted?.id ?? null);
      sending.current = false;
      if (!alive.current) return;
      if (done === false) { setError('generic'); return; }
      kept.delete(stamp);
      close();
    } catch (failure) {
      sending.current = false;
      if (!alive.current) return;
      if (REQUOTE.includes(failure?.code)) { setNotice('requoted'); await requestQuote(); return; }
      setError(errorKey(failure));
    } finally {
      sending.current = false;
      if (alive.current) setSubmitting(false);
    }
  };

  return { action, places, draft, place, selectedId, select, next, close, choices, pending, subjects, pickImage, generate, keep, clear, setSubject,
    upload, added, generating, estimate, quote, requestQuote, submitting, error, notice, confirm, offered: item => generationOffered(s.features, item) };
}
